import React, { useState } from "react";
import "./CRUDTable.css";

const CRUDTable = ({
  data,
  columns,
  onEdit,
  onDelete,
  onCreate,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  renderCustomCell = null,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [newItemData, setNewItemData] = useState({});

  const handleEditClick = (item) => {
    setEditingId(item._id);
    setEditingData({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSaveEdit = async () => {
    if (onEdit) {
      await onEdit(editingId, editingData);
    }
    setEditingId(null);
    setEditingData({});
  };

  const handleDelete = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo elemento?")) {
      if (onDelete) {
        await onDelete(id);
      }
    }
  };

  const handleCreateClick = () => {
    const initialData = {};
    columns.forEach((col) => {
      if (col.defaultValue !== undefined) {
        initialData[col.field] = col.defaultValue;
      } else if (col.type === "boolean") {
        initialData[col.field] = false;
      } else if (col.type === "number") {
        initialData[col.field] = 0;
      } else {
        initialData[col.field] = "";
      }
    });
    setNewItemData(initialData);
    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewItemData({});
  };

  const handleSaveCreate = async () => {
    if (onCreate) {
      await onCreate(newItemData);
    }
    setIsCreating(false);
    setNewItemData({});
  };

  const handleFieldChange = (field, value, isNew = false) => {
    if (isNew) {
      setNewItemData({ ...newItemData, [field]: value });
    } else {
      setEditingData({ ...editingData, [field]: value });
    }
  };

  const renderCell = (column, item, isEditing, isNew = false) => {
    const currentData = isNew ? newItemData : isEditing ? editingData : item;
    const value = currentData[column.field];

    // Se c'è un renderer custom, usalo
    if (renderCustomCell && !isEditing && !isNew) {
      const customRender = renderCustomCell(column, item);
      if (customRender !== null) return customRender;
    }

    // Rendering in modalità editing/creating
    if (isEditing || isNew) {
      if (column.editable === false) {
        return <span>{value}</span>;
      }

      if (column.type === "boolean") {
        return (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) =>
              handleFieldChange(column.field, e.target.checked, isNew)
            }
          />
        );
      }

      if (column.type === "select" && column.options) {
        return (
          <select
            value={value || ""}
            onChange={(e) =>
              handleFieldChange(column.field, e.target.value, isNew)
            }
          >
            {column.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      }

      if (column.type === "textarea") {
        return (
          <textarea
            value={value || ""}
            onChange={(e) =>
              handleFieldChange(column.field, e.target.value, isNew)
            }
            rows={3}
          />
        );
      }

      return (
        <input
          type={column.type || "text"}
          value={value || ""}
          onChange={(e) =>
            handleFieldChange(column.field, e.target.value, isNew)
          }
        />
      );
    }

    // Rendering normale
    if (column.type === "boolean") {
      return <span>{value ? "✓" : "✗"}</span>;
    }

    if (column.render) {
      return column.render(value, item);
    }

    return <span>{value}</span>;
  };

  return (
    <div className="crud-table-container">
      {canCreate && (
        <div className="crud-table-actions">
          <button onClick={handleCreateClick} className="btn-create">
            + Crea Nuovo
          </button>
        </div>
      )}

      <div className="crud-table-wrapper">
        <table className="crud-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.field}>{col.label}</th>
              ))}
              {(canEdit || canDelete) && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {isCreating && (
              <tr className="editing-row">
                {columns.map((col) => (
                  <td key={col.field}>{renderCell(col, {}, false, true)}</td>
                ))}
                <td>
                  <button onClick={handleSaveCreate} className="btn-save">
                    Salva
                  </button>
                  <button onClick={handleCancelCreate} className="btn-cancel">
                    Annulla
                  </button>
                </td>
              </tr>
            )}

            {data.map((item) => {
              const isEditing = editingId === item._id;
              return (
                <tr key={item._id} className={isEditing ? "editing-row" : ""}>
                  {columns.map((col) => (
                    <td key={col.field}>{renderCell(col, item, isEditing)}</td>
                  ))}
                  <td className="actions-cell">
                    {isEditing ? (
                      <>
                        <button onClick={handleSaveEdit} className="btn-save">
                          Salva
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="btn-cancel"
                        >
                          Annulla
                        </button>
                      </>
                    ) : (
                      <>
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(item)}
                            className="btn-edit"
                          >
                            Modifica
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="btn-delete"
                          >
                            Elimina
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.length === 0 && !isCreating && (
        <div className="empty-state">Nessun elemento trovato</div>
      )}
    </div>
  );
};

export default CRUDTable;
